import {
  Required,
  IsString,
  IsNumber,
  DataValidator,
  transformKeys,
  IsArray,
  ValidateNested,
  IsBoolean,
} from "@/utils/type-vaildator";
import api, { apiV2 } from "./api";

export interface AgentUserActors {
  id: string;
  model_id: string;
  name: string;
  gender: string;
  age_group: string;
  is_hd: boolean;
  is_pro: boolean;
  is_exclusive_pro: boolean;
  is_free_speech: boolean;
  situation?: string;
  image_url: string;
  compressed_actor_default_video_url: string;
  full_actor_default_video_url: string;
  sort_order: number;
  is_favorite: boolean;
  is_my_actor: boolean;
  is_free: string;
  tags?: Array<string>;
  generate_status: number;
  generate_type: number;
  created_at: string;
  source: string;
  default_audio: Default_audio;
};

export interface Audios {
  id: number;
  name: string;
  provider: string;
  sample_url: string;
  use_count: number;
  star_count: number;
  voice_tts_id: string;
  process_status: number;
  description: string;
  input_text: string;
  task_id: string;
  user_id: number;
  publish_state: string;
  created_at: string;
  updated_at: string;
};

export interface Default_audio {
  id: string;
  type: number;
  actor_id: string;
  voice_tts_id?: string;
  model_name: string;
  name: string;
  age_group: string;
  language: string;
  emotion: string;
  gender: string;
  speed: number;
  audio_url: string;
  duration: number;
  is_starred: boolean;
  is_selected: boolean;
  sort_order: number;
  use_count: number;
};

// 响应
export interface AgentUserActorDetailResponse {
  actors: AgentUserActors[];
  audios: Audios[];
};

// 转换并验证 AgentUserActor 数据

export class DefaultAudioData {
  @Required("Audio URL cannot be empty")
  @IsString("Audio URL must be a string")
  audioUrl: string;

  @Required("ID cannot be empty")
  @IsString("ID must be a string")
  id: string;

  @Required("Is starred cannot be empty")
  @IsBoolean("Is starred must be a boolean")
  isStarred: boolean;

  // @Required("Model name cannot be empty")
  @IsString("Model name must be a string")
  modelName: string;

  @Required("Name cannot be empty")
  @IsString("Name must be a string")
  name: string;

  @Required("Speed cannot be empty")
  @IsNumber("Speed must be a number")
  speed: number;

  // @Required("Voice TTS ID cannot be empty")
  @IsString("Voice TTS ID must be a string")
  voiceTtsId: string;
}

export class AgentUserActorData {
  @Required("ID cannot be empty")
  @IsString("ID must be a string")
  id: string;

  @Required("modelId cannot be empty")
  @IsString("modelId must be a string")
  modelId: string;

  @Required("Name cannot be empty")
  @IsString("Name must be a string")
  name: string;

  @Required("imageUrl cannot be empty")
  @IsString("imageUrl must be a string")
  imageUrl: string;

  @Required("generateStatus cannot be empty")
  @IsNumber("generateStatus status must be a number")
  generateStatus: number;

  @IsString("fullActorDefaultVideoUrl must be a string")
  fullActorDefaultVideoUrl: string;

  @IsString("createdAt must be a string")
  createdAt: string;

  @IsString("compressedActorDefaultVideoUrl must be a string")
  compressedActorDefaultVideoUrl: string;

  @IsArray("tags must be an array")
  tags?: Array<string>;

  @Required("isFavorite cannot be empty")
  @IsBoolean("isFavorite must be a boolean")
  isFavorite: boolean;

  @Required("isMyActor cannot be empty")
  @IsBoolean("isMyActor must be a boolean")
  isMyActor: boolean;

  // @Required("isPro cannot be empty")
  @IsBoolean("isPro must be a boolean")
  isPro: boolean;

  // @Required("isHD cannot be empty")
  @IsBoolean("isHD must be a boolean")
  isHD: boolean;

  @Required("generateType cannot be empty")
  @IsNumber("generateType must be a number")
  generateType: number;

  @Required("defaultAudio cannot be empty")
  @ValidateNested(DefaultAudioData)
  defaultAudio: DefaultAudioData;

  @Required("height cannot be empty")
  @IsNumber("height must be a number")
  height?: number;

  @Required("width cannot be empty")
  @IsNumber("width must be a number")
  width?: number;

  isUnusual?: boolean;
}

export class AgentUserVoiceData {
  @Required("Created at cannot be empty")
  @IsString("Created at must be a string")
  createdAt: string;

  @Required("Id cannot be empty")
  @IsString("Id must be a string")
  id: string;

  @Required("IsStarred cannot be empty")
  @IsBoolean("IsStarred must be a boolean")
  isStarred: boolean;

  @Required("Name cannot be empty")
  @IsString("Name must be a string")
  name: string;

  @Required("ProcessStatus cannot be empty")
  @IsNumber("ProcessStatus must be a number")
  processStatus: number;

  @Required("Provider cannot be empty")
  @IsString("Provider must be a string")
  provider: string;

  @Required("SampleUrl cannot be empty")
  @IsString("SampleUrl must be a string")
  sampleUrl: string;

  @Required("UserId cannot be empty")
  @IsString("UserId must be a string")
  userId: string;

  @Required("VoiceTtsId cannot be empty")
  @IsString("VoiceTtsId must be a string")
  voiceTtsId: string;
}

export class AgentUserActorDetailData {
  @IsArray("actors must be an array")
  actors: AgentUserActorData[] | null;

  @IsArray("audios must be an array")
  audios: AgentUserVoiceData[] | null;
}
// 转换并验证 AgentUserActor 数据
const transformAndValidateAgentUserActor = (
  response: AgentUserActorDetailResponse
): AgentUserActorDetailData => {
  const transformedData = transformKeys(response);

  try {
    const validData = DataValidator.validateOrThrow(transformedData, AgentUserActorDetailData);

    const data = {
      ...validData,
      actors: validData.actors || [],
      audios: validData.audios || [],
    };

    if (data.actors) {
      for (const actor of data.actors) {
        if (actor.generateStatus === 2) {
          try {
            DataValidator.validateOrThrow(actor, AgentUserActorData);
          } catch (error) {
            console.error("Assets Actor data validation failed:", error.message);
            // 数据异常特殊处理 标记
            actor.isUnusual = true;
          }
        }
      }
    }

    return data;
  } catch (error) {
    console.error("Assets Actor data validation failed:", error.message);
    throw error;
  }
};

/** 获取 Agent 用户演员列表 */
export const agentUserActorDetail = async ({
  userId,
  inputText,
  agentId,
}: { userId: string, inputText?: string, agentId?: string }): Promise<AgentUserActorDetailData> => {
  const response = await apiV2.post<AgentUserActorDetailResponse>('/agent/user/actor-detail', {
    user_id: userId,
    input_text: inputText,
  }, {
    headers: {
      'X-Agent-Id': agentId,
    }
  });
  const data = transformAndValidateAgentUserActor(response.data);
  return data;
};
